/**
 * PowerArQ Sticky Cart & Desktop Widget (Refined)
 * V2: Advanced Multi-Step Variant Selector
 */

class PowerArQStickyCart {
    constructor() {
        this.config = {
            position: 'left-bottom',
            zIndex: 2147483647,
        };

        this.state = {
            isOpen: false,
            product: window.powerarqCartWidgetConfig?.product || null,
            isMobile: window.matchMedia('(max-width: 767px)').matches,
            // Selection State
            selectedOptions: {}, // { "Color": "Black", "Size": "M" }
            currentVariant: null,
            // 各コンテキストの数量を保持（モーダル再描画でリセットされないように）
            quantities: { desktop: 1, mobile: 1, modal: 1 }
        };

        // Resize listener
        window.addEventListener('resize', () => {
            const isMobile = window.matchMedia('(max-width: 767px)').matches;
            if (this.state.isMobile !== isMobile) {
                this.state.isMobile = isMobile;
                this.reRender();
            }
        });

        this.init();
    }

    init() {
        this.createWidgetDOM();
        if (this.state.product) {
            // Pre-select first variant if available? Or leave empty?
            // Usually better to leave empty to force user choice if multiple variants.
            // If only 1 variant, select it.
            if (this.state.product.variants.length === 1) {
                this.state.currentVariant = this.state.product.variants[0];
            }
        }
    }

    reRender() {
        if (this.elements && this.elements.container) {
            this.elements.container.remove();
        }
        this.createWidgetDOM();
    }

    createWidgetDOM() {
        const container = document.createElement('div');
        container.id = 'powerarq-sticky-cart';
        container.className = 'pasc-container';

        // モバイルとデスクトップ両方で商品ページならスティッキーバーを表示
        if (this.state.product) {
            if (this.state.isMobile) {
                this.renderMobileStickyBar(container);
            } else {
                this.renderDesktopStickyBar(container);
            }
        }

        document.body.appendChild(container);
        this.elements = { container };
    }

    // --- Mobile Sticky Bar ---
    renderMobileStickyBar(container) {
        container.classList.add('pasc-mobile-sticky');
        const wrapper = document.createElement('div');
        wrapper.className = 'pasc-sticky-wrapper';

        const avatar = document.createElement('div');
        avatar.className = 'pasc-sticky-avatar';
        const img = document.createElement('img');
        img.src = window.powerarqCartWidgetConfig?.iconUrl || '';
        img.alt = 'Advisor';
        avatar.appendChild(img);

        const qtyStepper = this.createQtyStepper('mobile');

        const btn = document.createElement('button');
        btn.className = 'pasc-sticky-btn';

        // 完売(全バリアント在庫切れ)なら購入導線を出さない。
        // 再販お知らせフォームがあるページでは登録導線に切り替える
        const allSoldOut = this.state.product &&
            this.state.product.variants.every(v => !v.available);
        const restockEl = document.getElementById('RestockNotify');

        if (allSoldOut && restockEl) {
            btn.textContent = '販売開始をメールで受け取る';
            btn.classList.add('pasc-sticky-btn--restock'); // 注意喚起の揺れアニメ用
            btn.addEventListener('click', () => {
                restockEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
            qtyStepper.style.display = 'none';
        } else if (allSoldOut) {
            btn.textContent = '完売';
            btn.disabled = true;
            btn.style.opacity = '0.6';
            qtyStepper.style.display = 'none';
        } else {
            // 挙動はカート追加(複数バリアントは選択モーダル→カート追加)のため、ページ内CTAと文言を統一し混乱を防ぐ
            btn.textContent = 'カートに追加する';
            btn.addEventListener('click', () => this.handleMobileAddToCart());
        }

        wrapper.appendChild(avatar);
        wrapper.appendChild(qtyStepper);
        wrapper.appendChild(btn);
        container.appendChild(wrapper);
    }

    handleMobileAddToCart() {
        if (!this.state.product) return;

        const variants = this.state.product.variants;
        if (variants.length > 1) {
            // モーダルにモバイルの数量を引き継ぐ
            this.state.quantities.modal = this.state.quantities.mobile || 1;
            this.openAdvancedVariantModal();
        } else {
            const qty = this.getStickyQty('mobile');
            this.addToCart(variants[0].id, qty, true);
        }
    }

    // --- Advanced Variant Modal (Multi-Step) ---
    openAdvancedVariantModal() {
        const existing = document.getElementById('pasc-variant-modal');
        if (existing) existing.remove();

        const modal = document.createElement('div');
        modal.id = 'pasc-variant-modal';
        modal.className = 'pasc-modal-overlay';

        const content = document.createElement('div');
        content.className = 'pasc-modal-content';

        this.modalContent = content; // Store ref for re-rendering content
        this.renderModalContent(content, modal);

        modal.appendChild(content);
        document.body.appendChild(modal);

        modal.addEventListener('click', (e) => {
            if (e.target === modal) modal.remove();
        });
    }

    renderModalContent(contentContainer, modalInstance) {
        contentContainer.innerHTML = ''; // Clear previous render

        // 1. Header with Close Button
        const header = document.createElement('div');
        header.className = 'pasc-modal-header';
        // header.innerHTML = `<h3>オプションを選択</h3>`;
        // Close button
        const closeBtn = document.createElement('button');
        closeBtn.className = 'pasc-modal-close-icon';
        closeBtn.innerHTML = '&times;';
        closeBtn.onclick = () => modalInstance.remove();
        header.appendChild(closeBtn);
        contentContainer.appendChild(header);

        // 2. Summary Box
        // Shows "Selection: Color Black / Size M - Price"
        const summaryBox = document.createElement('div');
        summaryBox.className = 'pasc-summary-box';
        const currentPrice = this.state.currentVariant ? this.formatMoney(this.state.currentVariant.price) : '選択してください';
        const selectionText = this.getSelectionText();
        summaryBox.innerHTML = `
          <div class="pasc-summary-label">選択中:</div>
          <div class="pasc-summary-value">${selectionText}</div>
          <div class="pasc-summary-price">${currentPrice}</div>
      `;
        contentContainer.appendChild(summaryBox);

        // 3. Option Steps
        const scrollArea = document.createElement('div');
        scrollArea.className = 'pasc-scroll-area';

        // voltera-jacket/voltera-pants: 3軸を2段階で完結（STEP2でoption2+option3を結合表示）
        const twoStepHandles = ['voltera-jacket', 'voltera-pants'];
        const isTwoStepProduct = twoStepHandles.includes(this.state.product.handle);
        const optionsToShow = isTwoStepProduct && this.state.product.options.length >= 3
            ? [this.state.product.options[0]] // STEP1のみループ、STEP2は別処理
            : this.state.product.options;

        optionsToShow.forEach((optionName, index) => {
            const stepContainer = document.createElement('div');
            stepContainer.className = 'pasc-step-container';

            const stepLabel = document.createElement('div');
            stepLabel.className = 'pasc-step-label';
            stepLabel.textContent = `[STEP${index + 1}] ${optionName}を選択してください`;
            stepContainer.appendChild(stepLabel);

            const optionsGrid = document.createElement('div');
            optionsGrid.className = 'pasc-options-grid';

            // Get values for this option
            // We need to filter values if they are dependent?
            // For simplicity in standard Shopify, we show all values for the option,
            // usually standard themes filter Step 2 based on Step 1.
            // Let's implement "filtering Step 2 based on Step 1".

            const values = this.getValuesForOption(index);

            values.forEach(value => {
                const btn = document.createElement('button');
                btn.className = 'pasc-option-btn';

                // Check if selected
                const isSelected = this.state.selectedOptions[optionName] === value;
                if (isSelected) btn.classList.add('selected');

                // Check availability (Mock or Real logic)
                // Complex logic: Check if (Selected Previous Options + This Value) has ANY valid variant.
                const isAvailable = this.checkAvailability(optionName, value);
                if (!isAvailable) {
                    btn.classList.add('disabled');
                    btn.disabled = true;
                }

                // Find variant image for this option value
                const variantWithImage = this.getVariantImageForOptionValue(index, value);
                let imgHTML = '';
                if (variantWithImage) {
                    imgHTML = `<img class="pasc-opt-img" src="${variantWithImage}" alt="${value}">`;
                }

                // Inner Content
                let btnHTML = `${imgHTML}<span class="pasc-opt-name">${value}</span>`;

                // If last Step, show Price/Stock status for this specific variant combo
                if (index === this.state.product.options.length - 1) {
                    const variant = this.getVariantForPotentialSelection(optionName, value);
                    if (variant) {
                        const stockText = variant.available ? '<span class="stock-in">✓ 在庫あり</span>' : '<span class="stock-out">× 在庫切れ</span>';
                        const priceText = this.formatMoney(variant.price);
                        btnHTML += `<div class="pasc-opt-meta">${priceText}</div>`;
                        btnHTML += `<div class="pasc-opt-stock">${stockText}</div>`;
                    }
                }

                btn.innerHTML = btnHTML;

                btn.onclick = () => {
                    this.handleOptionSelect(optionName, value);
                    this.renderModalContent(contentContainer, modalInstance); // Re-render whole modal
                };

                optionsGrid.appendChild(btn);
            });

            stepContainer.appendChild(optionsGrid);
            scrollArea.appendChild(stepContainer);
        });

        // voltera-jacket/voltera-pants専用: STEP2でoption2+option3を結合表示
        if (isTwoStepProduct && this.state.product.options.length >= 3) {
            const step2Container = document.createElement('div');
            step2Container.className = 'pasc-step-container';

            const step2Label = document.createElement('div');
            step2Label.className = 'pasc-step-label';
            step2Label.textContent = '[STEP2] オプションを選択してください';
            step2Container.appendChild(step2Label);

            const step2Grid = document.createElement('div');
            step2Grid.className = 'pasc-options-grid';

            // STEP1で選択されたサイズに基づいてバリエーションをフィルタリング
            const selectedOption1 = this.state.selectedOptions[this.state.product.options[0]];
            const filteredVariants = selectedOption1
                ? this.state.product.variants.filter(v => v.option1 === selectedOption1)
                : [];

            filteredVariants.forEach(variant => {
                const btn = document.createElement('button');
                btn.className = 'pasc-option-btn';

                // option2 + option3 を結合した表示名
                const displayName = variant.option3 && variant.option3 !== 'なし'
                    ? `${variant.option2} / ${variant.option3}`
                    : variant.option2;

                // 選択状態の判定（option2とoption3両方が一致）
                const isSelected = this.state.selectedOptions[this.state.product.options[1]] === variant.option2 &&
                                   this.state.selectedOptions[this.state.product.options[2]] === variant.option3;
                if (isSelected) btn.classList.add('selected');

                // 在庫状態
                if (!variant.available) {
                    btn.classList.add('disabled');
                    btn.disabled = true;
                }

                // バリエーション画像
                let imgHTML = '';
                if (variant.featured_image && variant.featured_image.src) {
                    const imgSrc = variant.featured_image.src.replace(/\.([^.]+)$/, '_100x100.$1');
                    imgHTML = `<img class="pasc-opt-img" src="${imgSrc}" alt="${displayName}">`;
                }

                // 価格と在庫状態
                const stockText = variant.available ? '<span class="stock-in">✓ 在庫あり</span>' : '<span class="stock-out">× 在庫切れ</span>';
                const priceText = this.formatMoney(variant.price);

                btn.innerHTML = `${imgHTML}<span class="pasc-opt-name">${displayName}</span><div class="pasc-opt-meta">${priceText}</div><div class="pasc-opt-stock">${stockText}</div>`;

                btn.onclick = () => {
                    // option2とoption3を同時に選択
                    this.state.selectedOptions[this.state.product.options[1]] = variant.option2;
                    this.state.selectedOptions[this.state.product.options[2]] = variant.option3;
                    this.state.currentVariant = variant;
                    this.renderModalContent(contentContainer, modalInstance);
                };

                step2Grid.appendChild(btn);
            });

            step2Container.appendChild(step2Grid);
            scrollArea.appendChild(step2Container);
        }

        contentContainer.appendChild(scrollArea);

        // 4. Sticky Footer in Modal (Buy Button)
        const footer = document.createElement('div');
        footer.className = 'pasc-modal-footer';

        const buyBtn = document.createElement('button');
        buyBtn.className = 'pasc-modal-buy-btn';
        buyBtn.textContent = 'カートに追加する';

        // モーダル内にも数量ステッパーを追加
        const modalQty = this.createQtyStepper('modal');
        footer.appendChild(modalQty);

        if (this.state.currentVariant && this.state.currentVariant.available) {
            buyBtn.disabled = false;
            buyBtn.onclick = () => {
                const qty = this.getStickyQty('modal');
                this.addToCart(this.state.currentVariant.id, qty, true);
                modalInstance.remove();
            };
        } else {
            buyBtn.disabled = true;
            buyBtn.textContent = this.state.currentVariant ? '在庫切れ' : '選択してください';
            buyBtn.style.opacity = '0.6';
        }

        footer.appendChild(buyBtn);
        contentContainer.appendChild(footer);
    }

    // Logic Helpers
    getValuesForOption(optionIndex) {
        // Return unique values for this option index
        // Simple approach: Return all values from product.options[i].values?
        // But `product.options` is just an array of names in the JS JSON unless we parse variants.
        // Wait, {{ product | json }} usually has .options as ["Color", "Size"]
        // and .variants as objects.

        // We need to extract unique values from variants for this index.
        // option1 = index 0, option2 = index 1
        const key = `option${optionIndex + 1}`;
        const values = new Set();
        this.state.product.variants.forEach(v => {
            values.add(v[key]);
        });
        return Array.from(values);
    }

    getVariantImageForOptionValue(optionIndex, value) {
        // Find variant with this option value that has an image
        // For 2nd+ options, also consider already selected previous options
        const key = `option${optionIndex + 1}`;

        // Build filter based on current selections + this value
        const variant = this.state.product.variants.find(v => {
            // Must match the current option value
            if (v[key] !== value) return false;

            // Must have an image
            if (!v.featured_image || !v.featured_image.src) return false;

            // For options after the first, also match already selected options
            for (let i = 0; i < optionIndex; i++) {
                const prevOptName = this.state.product.options[i];
                const prevOptKey = `option${i + 1}`;
                if (this.state.selectedOptions[prevOptName] &&
                    v[prevOptKey] !== this.state.selectedOptions[prevOptName]) {
                    return false;
                }
            }

            return true;
        });

        if (variant && variant.featured_image) {
            // Return smaller size for thumbnail
            return variant.featured_image.src.replace(/\.([^.]+)$/, '_100x100.$1');
        }

        // Fallback: If no image found with current selection, try to find any image for this option value
        const fallbackVariant = this.state.product.variants.find(v => {
            return v[key] === value && v.featured_image && v.featured_image.src;
        });

        if (fallbackVariant && fallbackVariant.featured_image) {
            return fallbackVariant.featured_image.src.replace(/\.([^.]+)$/, '_100x100.$1');
        }

        return null;
    }

    checkAvailability(currentOptionName, currentValue) {
        // Copy current selection
        const potentiallySelected = { ...this.state.selectedOptions };
        potentiallySelected[currentOptionName] = currentValue;

        return this.state.product.variants.some(v => {
            // Must be available (in stock)
            if (!v.available) return false;

            // Check if variant matches all potential selection keys
            let match = true;
            this.state.product.options.forEach((optName, idx) => {
                const optKey = `option${idx + 1}`;
                if (potentiallySelected[optName] && v[optKey] !== potentiallySelected[optName]) {
                    match = false;
                }
            });
            return match;
        });
    }

    getVariantForPotentialSelection(currentOptionName, currentValue) {
        // Construct a full selection set assuming this value is chosen
        const tempSelection = { ...this.state.selectedOptions };
        tempSelection[currentOptionName] = currentValue;

        // Check if we have a FULL selection (all options chosen)
        if (Object.keys(tempSelection).length < this.state.product.options.length) {
            return null; // Not fully selected yet
        }

        // Find the specific variant
        return this.state.product.variants.find(v => {
            return this.state.product.options.every((optName, idx) => {
                const optKey = `option${idx + 1}`;
                return v[optKey] === tempSelection[optName];
            });
        });
    }

    handleOptionSelect(optionName, value) {
        // Update Selection
        this.state.selectedOptions[optionName] = value;

        // If this was Step 1, and Step 2 was already selected but is now invalid, clear Step 2?
        // For now, simpler to just keep it and let the re-render show it as invalid/unavailable if needed.
        // But clearing downstream selections is better UX.
        const optIndex = this.state.product.options.indexOf(optionName);
        // Clear options after this index
        for (let i = optIndex + 1; i < this.state.product.options.length; i++) {
            const nextOptName = this.state.product.options[i];
            delete this.state.selectedOptions[nextOptName];
        }

        // Check if full selection is made
        if (Object.keys(this.state.selectedOptions).length === this.state.product.options.length) {
            // Find variant
            this.state.currentVariant = this.state.product.variants.find(v => {
                return this.state.product.options.every((optName, idx) => {
                    const optKey = `option${idx + 1}`;
                    return v[optKey] === this.state.selectedOptions[optName];
                });
            });
        } else {
            this.state.currentVariant = null;
        }
    }

    getSelectionText() {
        // "Black / M"
        const parts = [];
        this.state.product.options.forEach(optName => {
            if (this.state.selectedOptions[optName]) {
                parts.push(this.state.selectedOptions[optName]);
            }
        });
        return parts.join(' / ') || '未選択';
    }

    // --- End Variant Logic ---

    // 数量ステッパーUI生成（stateから初期値取得、変更時にstateへ保存）
    createQtyStepper(context) {
        const self = this;
        const stepper = document.createElement('div');
        stepper.className = 'pasc-qty-stepper';
        stepper.dataset.context = context;

        const minus = document.createElement('button');
        minus.className = 'pasc-qty-btn pasc-qty-minus';
        minus.textContent = '−';
        minus.type = 'button';

        const display = document.createElement('span');
        display.className = 'pasc-qty-value';

        const plus = document.createElement('button');
        plus.className = 'pasc-qty-btn pasc-qty-plus';
        plus.textContent = '＋';
        plus.type = 'button';

        // stateから初期値を取得
        let qty = self.state.quantities[context] || 1;

        const update = () => {
            display.textContent = qty;
            minus.disabled = qty <= 1;
            stepper.dataset.qty = qty;
            self.state.quantities[context] = qty;
        };

        minus.addEventListener('click', (e) => {
            e.stopPropagation();
            if (qty > 1) { qty--; update(); }
        });

        plus.addEventListener('click', (e) => {
            e.stopPropagation();
            qty++;
            update();
        });

        stepper.appendChild(minus);
        stepper.appendChild(display);
        stepper.appendChild(plus);
        update();
        return stepper;
    }

    // stateから数量を取得
    getStickyQty(context) {
        return this.state.quantities[context] || 1;
    }

    async addToCart(variantId, qty, isDirectCheckout = false) {
        try {
            if (isDirectCheckout) {
                const btn = document.querySelector('.pasc-sticky-btn') || document.querySelector('.pasc-modal-buy-btn');
                if (btn) btn.textContent = '処理中...';
            }

            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] })
            });

            if (response.ok) {
                if (isDirectCheckout) {
                    window.location.href = '/checkout';
                } else {
                    this.openPanel(); // Fallback for desktop if used there
                }
            } else {
                alert('追加に失敗しました。');
                const btn = document.querySelector('.pasc-sticky-btn') || document.querySelector('.pasc-modal-buy-btn');
                if (btn) btn.textContent = '追加する';
            }
        } catch (e) {
            console.error(e);
            alert('エラーが発生しました。');
        }
    }

    formatMoney(cents) {
        if (!cents) return '';
        const yen = Math.floor(cents / 100);
        return `¥${yen.toLocaleString('ja-JP')}`;
    }

    // --- Desktop Widget Stubs (Unchanged) ---
    // --- Desktop Sticky Bar (Right Side - Rakuten Style) ---
    renderDesktopStickyBar(container) {
        const product = this.state.product;

        // 完売判定(モバイル版と同じ基準)。
        // 再販お知らせフォームの無いただの完売商品では、購入ポップアップ自体を出さない
        const allSoldOut = product && product.variants &&
            product.variants.every(v => !v.available);
        const restockEl = document.getElementById('RestockNotify');
        if (allSoldOut && !restockEl) return;

        container.classList.add('pasc-desktop-sticky');
        const wrapper = document.createElement('div');
        wrapper.className = 'pasc-sticky-wrapper';

        // 商品画像（メイン画像を使用）
        const avatar = document.createElement('div');
        avatar.className = 'pasc-sticky-avatar';
        const img = document.createElement('img');
        // 商品のメイン画像を取得（Shopify product JSONの構造に対応）
        let productImage = '';
        if (product) {
            productImage = product.featured_image ||
                          (product.images && product.images[0]) ||
                          (product.media && product.media[0] && product.media[0].src) || '';
        }
        if (!productImage) {
            productImage = window.powerarqCartWidgetConfig?.iconUrl || '';
        }
        img.src = productImage;
        img.alt = product?.title || 'Product';
        avatar.appendChild(img);

        // 商品名
        const title = document.createElement('div');
        title.className = 'pasc-sticky-title';
        title.textContent = product?.title || '';

        // 価格（バリエーション対応・セール対応）
        const priceContainer = document.createElement('div');
        priceContainer.className = 'pasc-sticky-price';

        if (product) {
            const variants = product.variants || [];
            const prices = variants.map(v => v.price).filter(p => p > 0);
            const compareAtPrices = variants.map(v => v.compare_at_price).filter(p => p > 0);

            const minPrice = prices.length > 0 ? Math.min(...prices) : (product.price || 0);
            const maxPrice = prices.length > 0 ? Math.max(...prices) : minPrice;
            const hasVariantPriceRange = minPrice !== maxPrice;

            // セール判定（compare_at_priceがある場合）
            const compareAtPrice = compareAtPrices.length > 0 ? Math.max(...compareAtPrices) : 0;
            const isOnSale = compareAtPrice > 0 && compareAtPrice > minPrice;

            if (isOnSale) {
                // セール価格表示
                const originalPrice = document.createElement('span');
                originalPrice.className = 'pasc-price-original';
                originalPrice.textContent = this.formatMoney(compareAtPrice);
                priceContainer.appendChild(originalPrice);

                const salePrice = document.createElement('span');
                salePrice.className = 'pasc-price-sale';
                salePrice.textContent = this.formatMoney(minPrice) + (hasVariantPriceRange ? '~' : '');
                priceContainer.appendChild(salePrice);
            } else {
                // 通常価格表示
                priceContainer.textContent = this.formatMoney(minPrice) + (hasVariantPriceRange ? '~' : '');
            }
        }

        // 数量ステッパー + 購入ボタン
        // (再販お知らせ対象の完売商品では、購入UIの代わりにメール登録導線を出す)
        const actionRow = document.createElement('div');
        actionRow.className = 'pasc-sticky-action-row';

        if (allSoldOut && restockEl) {
            const btn = document.createElement('button');
            btn.className = 'pasc-sticky-btn pasc-sticky-btn--restock';
            btn.textContent = '販売開始をメールで受け取る';
            btn.addEventListener('click', () => {
                restockEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
            actionRow.appendChild(btn);
        } else {
            const qtyStepper = this.createQtyStepper('desktop');
            actionRow.appendChild(qtyStepper);

            const btn = document.createElement('button');
            btn.className = 'pasc-sticky-btn';
            btn.textContent = '購入する';
            btn.addEventListener('click', () => this.handleDesktopAddToCart());
            actionRow.appendChild(btn);
        }

        wrapper.appendChild(avatar);
        wrapper.appendChild(title);
        wrapper.appendChild(priceContainer);
        wrapper.appendChild(actionRow);
        container.appendChild(wrapper);

        // スクロールで表示/非表示を切り替え
        this.setupDesktopScrollListener(container);
    }

    // デスクトップ版: 商品画像がビューポートを超えたらスティッキーバーを表示
    setupDesktopScrollListener(container) {
        // 商品画像/カートボタンのセレクタ（複数候補から探す）
        const triggerSelectors = [
            '.product-single__photos',
            '.product__photos',
            '.product-single__media-wrapper',
            '.product-featured-img',
            '[data-product-photos]',
            '.product-single__media',
            '.product__media-list',
            '.product-form__cart-submit',
            '[name="add"]',
            '.product-form'
        ];

        let triggerEl = null;
        for (const selector of triggerSelectors) {
            triggerEl = document.querySelector(selector);
            if (triggerEl) break;
        }

        if (!triggerEl) {
            // トリガー要素が見つからない場合は常に表示
            container.classList.add('pasc-desktop-sticky--visible');
            return;
        }

        const checkVisibility = () => {
            const rect = triggerEl.getBoundingClientRect();
            // トリガー要素の下端がビューポートの半分より上にきたら表示
            const threshold = window.innerHeight * 0.5;
            if (rect.bottom < threshold) {
                container.classList.add('pasc-desktop-sticky--visible');
            } else {
                container.classList.remove('pasc-desktop-sticky--visible');
            }
        };

        // 初回チェック
        checkVisibility();

        // スクロール時にチェック（パフォーマンス対策でthrottle）
        let ticking = false;
        window.addEventListener('scroll', () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    checkVisibility();
                    ticking = false;
                });
                ticking = true;
            }
        }, { passive: true });
    }

    handleDesktopAddToCart() {
        if (!this.state.product) return;

        const variants = this.state.product.variants;
        if (variants.length > 1) {
            // モーダルにデスクトップの数量を引き継ぐ
            this.state.quantities.modal = this.state.quantities.desktop || 1;
            this.openAdvancedVariantModal();
        } else {
            const qty = this.getStickyQty('desktop');
            this.addToCart(variants[0].id, qty, true);
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { window.powerarqStickyCart = new PowerArQStickyCart(); });
} else {
    window.powerarqStickyCart = new PowerArQStickyCart();
}
